import { apiSlice } from "../../api-slice";
import { PolicyBody, PolicyTATBody } from "./policy-body";
import { ContractTatData, DefaultPolicyResponse, DocumentCodeResponse, DocumentCodeResponseData, PolicyData, PolicyResponse, PolicyTATResponse, SigningAddressResponse, SigningAddressResponseData } from "./policy-response";

export type PolicyContractType  =
    | "principal"
    | "outstanding"
    | "gpa";

interface PolicyParams {
    policyId: number;
    type: PolicyContractType ;
}

interface GCLIPPolicyMutationParams {
    policyId: number;
    type: PolicyContractType ;
    body: PolicyBody;
}

export const policyAPI = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // GET
        getPolicy: builder.query<PolicyData, PolicyParams>({
            query: ({ policyId, type }) => `/api/ebam/${policyId}/policy-contract/${type}`,
            transformResponse: (response: PolicyResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: 'LIST', },
                { type: "Policy", id: policyId, },
            ],
        }),
        // POST
        inputPolicy: builder.mutation<DefaultPolicyResponse, GCLIPPolicyMutationParams>({
            query: ({ policyId, type, body }) => ({
                url: `/api/ebam/${policyId}/policy-contract/${type}`,
                method: "POST",
                body,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: policyId },
                { type: "Policy", id: 'LIST', },
            ],
        }),
        // PUT
        updatePolicy: builder.mutation<DefaultPolicyResponse, GCLIPPolicyMutationParams>({
            query: ({ policyId, type, body }) => ({
                url: `/api/ebam/${policyId}/policy-contract/${type}`,
                method: "PUT",
                body,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: policyId, },
                { type: "Policy", id: 'LIST', },
            ],
        }),
        // GET SIGNING ADDRESS
        getPolicySigningAddress: builder.query<SigningAddressResponseData, GCLIPPolicyMutationParams>({
            query: ({ policyId }) => `/api/ebam/${policyId}/policy-contract/signing-address`,
            transformResponse: (response: SigningAddressResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: policyId },
                { type: "Policy", id: 'LIST', },
            ],
        }),
        // PUT SIGNING ADDRESS
        updatePolicySigningAddress: builder.mutation<DefaultPolicyResponse, GCLIPPolicyMutationParams>({
            query: ({ policyId, body }) => ({
                url: `/api/ebam/${policyId}/policy-contract/signing-address`,
                method: "PUT",
                body,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: policyId },
                { type: "Policy", id: 'LIST', },
            ],
        }),
        // GET DOC CODE
        getPolicyDocCode: builder.query<DocumentCodeResponseData, GCLIPPolicyMutationParams>({
            query: ({ policyId }) => `/api/ebam/${policyId}/policy-contract/doc-code`,
            transformResponse: (response: DocumentCodeResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: policyId },
                { type: "Policy", id: 'LIST', },
            ],
        }),
        // PUT DOC CODE
        updatePolicyDocCode: builder.mutation<DefaultPolicyResponse, GCLIPPolicyMutationParams>({
            query: ({ policyId, body }) => ({
                url: `/api/ebam/${policyId}/policy-contract/doc-code`,
                method: "PUT",
                body,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { policyId, type }) => [
                { type: "Policy", id: policyId },
                { type: "Policy", id: 'LIST', },
            ],
        }),
        getPolicyTAT: builder.query<ContractTatData, { policyId: number }>({
            query: ({ policyId }) => `/api/ebam/${policyId}/policy-contract/tat`,
            transformResponse: (response: PolicyTATResponse) => response.data,
            transformErrorResponse: (response: any) => response.data,
            providesTags: (_result, _error, { policyId }) => [
                { type: 'PolicyTAT', id: policyId },
                { type: 'PolicyTAT', id: 'LIST' },
            ],
        }),
        updatePolicyTAT: builder.mutation<DefaultPolicyResponse, { body: PolicyTATBody; policyId: number }>({
            query: ({ policyId, body }) => ({
                url: `/api/ebam/${policyId}/policy-contract/tat`,
                method: 'PUT',
                body,
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { policyId }) => [
                { type: 'PolicyTAT', id: policyId },
                { type: 'PolicyTAT', id: 'LIST' },
            ],
        }),

        setProposalEffectiveDate: builder.mutation<DefaultPolicyResponse, { proposalId: number; base_date: string}>({
            query: ({ proposalId, base_date }) => ({
                url: `/api/financial-insurance/${proposalId}/effective-date`,
                method: 'PUT',
                body: { base_date },
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { proposalId }) => [
                { type: 'Proposal', id: proposalId },
                { type: 'Proposal', id: 'LIST' },
                { type: 'Policy', id: proposalId },
                { type: 'Policy', id: 'LIST' },
            ],
        }),

        submitPolicyContract: builder.mutation<DefaultPolicyResponse, { policyId: number, notes: null | string }>({
            query: ({ policyId, notes }) => ({
                url: `/api/ebam/${policyId}/policy-contract/issue`,
                method: 'PUT',
                body: { notes }
            }),
            transformErrorResponse: (response: any) => response.data,
            invalidatesTags: (_result, _error, { policyId }) => [
                { type: 'Policy', id: policyId },
                { type: 'Policy', id: 'LIST' },
                { type: 'Proposal', id: policyId },
                { type: 'Proposal', id: 'LIST' },
            ],
        }),


    }),
});

export const {
    useGetPolicyQuery,
    useInputPolicyMutation,
    useUpdatePolicyMutation,

    useGetPolicySigningAddressQuery,
    useUpdatePolicySigningAddressMutation,

    useGetPolicyDocCodeQuery,
    useUpdatePolicyDocCodeMutation,

    useGetPolicyTATQuery,
    useUpdatePolicyTATMutation,

    // UPDATE PROPOSAL EFFECTIVE DATE
    useSetProposalEffectiveDateMutation,

    useSubmitPolicyContractMutation,
} = policyAPI;